import React, { useState, useEffect } from 'react';
import { message, DatePicker } from 'antd';
const { RangePicker } = DatePicker;
import {
  Container, 
  Box, 
  Card, 
  CardActionArea, 
  CardHeader, 
  CardContent, 
  Typography, 
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';

// ICONOS MUI
import CakeIcon from '@mui/icons-material/Cake';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';

import LoadingInformation from '../../Components/LoadingInformation';

function ViewBirthdayList() {
  const [birthdayList, setBirthdayList] = useState([]);
  const [filteredBirthdayList, setFilteredBirthdayList] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  
  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState([null, null]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const token = localStorage.getItem('token');

  const fetchBirthdayList = async () => {
    try {
      const response = await fetch(`https://dprincess-training.servehttp.com/birthdays/closest`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setBirthdayList(data);
        setFilteredBirthdayList(data);
        setLoading(false);
      } else {
        message.error('Error al cargar la lista de cumpleaños');
        messageApi.open({
          type: 'error',
          content: 'Error al cargar la lista de cumpleaños',
        });
      }
    } catch (error) {
      setLoading(false);
      console.error(`Error de conexión al servidor: ${error}`);
      messageApi.open({
        type: 'error',
        content: 'Error de conexión al servidor',
      });
    }
  }

  // Leer parámetros de la URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || '';
    const payment = params.get('payment') || '';
    const startDate = params.get('start');
    const endDate = params.get('end');

    setSearchTerm(search);
    setPaymentFilter(payment);
    setDateRangeFilter([
      startDate ? dayjs(startDate).toDate() : null,
      endDate ? dayjs(endDate).toDate() : null,
    ]);
  }, [location.search]);

  // Actualizar parámetros de la URL
  const updateURLParams = () => {
    const params = new URLSearchParams();

    if (searchTerm) params.set('search', searchTerm);
    if (paymentFilter) params.set('payment', paymentFilter);
    if (dateRangeFilter[0]) params.set('start', dayjs(dateRangeFilter[0]).format('YYYY-MM-DD'));
    if (dateRangeFilter[1]) params.set('end', dayjs(dateRangeFilter[1]).format('YYYY-MM-DD'));

    navigate(`?${params.toString()}`);
  };

  useEffect(() => {
    updateURLParams();
  }, [searchTerm, paymentFilter, dateRangeFilter]);

  // Función para filtrar cumpleaños
  const filterBirthdays = () => {
    let filtered = birthdayList.filter(birthday => {
      const matchesSearch = !searchTerm || 
        birthday.XVName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        birthday.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        birthday.eventTitle?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesPayment = !paymentFilter || birthday.paymentStatus === paymentFilter;

      // Validar que las fechas sean instancias válidas de dayjs
      const startDate = dateRangeFilter[0] ? dayjs(dateRangeFilter[0]) : null;
      const endDate = dateRangeFilter[1] ? dayjs(dateRangeFilter[1]) : null;

      const matchesDateRange = !startDate || !endDate || (
        dayjs(birthday.birthday).isValid() &&
        dayjs(birthday.birthday).isAfter(startDate.subtract(1, 'day')) &&
        dayjs(birthday.birthday).isBefore(endDate.add(1, 'day'))
      );

      return matchesSearch && matchesPayment && matchesDateRange;
    });

    setFilteredBirthdayList(filtered);
  };

  // Función para limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setPaymentFilter('');
    setDateRangeFilter([null, null]);
    setFilteredBirthdayList(birthdayList);
  };

  useEffect(() => {
    fetchBirthdayList();
  }, []);

  // Effect para filtrar cuando cambien los filtros
  useEffect(() => {
    filterBirthdays();
  }, [searchTerm, paymentFilter, dateRangeFilter, birthdayList]);

  if (loading) {
    return <LoadingInformation />;
  }

  return (
    <div style={{ background: '#f3e8ff', minHeight: '90vh', overflow: 'auto' }}>
      <Container
      sx={{
        padding: '32px 0',
        minHeight: '100vh',
      }}
      maxWidth="lg"
    >
      {contextHolder}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Typography
          variant="h4"
          sx={{
            marginBottom: '28px',
            color: '#4b0082',
            fontWeight: 700,
            letterSpacing: '0.5px',
            textAlign: 'center'
          }}
        >
          Lista de Cumpleaños próximos
        </Typography>

        {/* Sección de Filtros */}
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: '1200px',
            mb: 3,
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid #f759ab'
          }}
        >
          {/* Header de Filtros */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              bgcolor: 'linear-gradient(90deg, #f759ab 0%, #4b0082 100%)',
              background: 'linear-gradient(90deg, #f759ab 0%, #4b0082 100%)',
              color: 'white',
              cursor: 'pointer'
            }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Filtros de Búsqueda
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(searchTerm || paymentFilter || (dateRangeFilter[0] && dateRangeFilter[1])) && (
                <Chip
                  label={`${filteredBirthdayList.length} resultados`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              )}
              <Typography variant="body2">
                {showFilters ? 'Ocultar' : 'Mostrar'}
              </Typography>
            </Box>
          </Box>

          {/* Contenido de Filtros */}
          {showFilters && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={isMobile ? 2 : 3}>
                {/* Búsqueda General */}
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="Buscar por nombre, cliente o evento"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: '#f759ab', mr: 1 }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: '#f759ab' },
                        '&.Mui-focused fieldset': { borderColor: '#f759ab' }
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#f759ab' }
                    }}
                  />
                </Grid>

                {/* Filtro por Rango de Fechas de Cumpleaños */}
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon sx={{ color: '#f759ab' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Rango de Fechas de Cumpleaños
                      </Typography>
                      <RangePicker
                        value={dateRangeFilter.map(date => (date ? dayjs(date) : null))} // Convertir a instancias de dayjs o null
                        onChange={(dates) => setDateRangeFilter(dates ? dates.map(date => (date ? dayjs(date).toDate() : null)) : [null, null])} // Convertir a Date o null
                        placeholder={['Fecha inicio', 'Fecha fin']}
                        style={{ width: '100%' }}
                        format="DD/MM/YYYY"
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Filtro por Pago */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ '&.Mui-focused': { color: '#f759ab' } }}>
                      Estado de Pago
                    </InputLabel>
                    <Select
                      value={paymentFilter}
                      label="Estado de Pago"
                      onChange={(e) => setPaymentFilter(e.target.value)}
                      sx={{
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                      }}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="pendiente">Pendiente</MenuItem>
                      <MenuItem value="completado">Completado</MenuItem>
                      <MenuItem value="parcial">Parcial</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Botón para limpiar filtros */}
              {(searchTerm || (dateRangeFilter[0] && dateRangeFilter[1]) || paymentFilter) && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    sx={{
                      color: '#f759ab',
                      borderColor: '#f759ab',
                      '&:hover': {
                        backgroundColor: 'rgba(247, 89, 171, 0.1)',
                        borderColor: '#f759ab'
                      }
                    }}
                  >
                    Limpiar Filtros
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {/* Resultados */}
        {filteredBirthdayList.length > 0 ? filteredBirthdayList.map((birthday, index) => (
          birthday.XVName !== 'No aplica' && (
          <Grid container spacing={1} key={index} sx={{ marginBottom: '24px', justifyContent: 'center' }}>
            <Grid item xs={12} md={10} sx={{ justifyItems: 'center' }}>
              <Card
                sx={{
                  width: "95%",
                  borderRadius: '18px',
                  border: '1.5px solid #f759ab',
                  boxShadow: '0 4px 16px 0 rgba(247,89,171,0.10)',
                  background: 'linear-gradient(90deg, #fff 80%, #f3e8ff 100%)',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: '0 8px 32px 0 rgba(247,89,171,0.18)',
                    borderColor: '#f759ab'
                  },
                  position: 'relative',
                }}
              >
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CakeIcon sx={{ color: '#f759ab', fontSize: 32 }} />
                      <Typography
                        sx={{
                          color: '#4b0082',
                          fontWeight: 700,
                          fontSize: '1.4rem',
                          m: 0
                        }}
                        gutterBottom
                        component="div"
                      >
                        {birthday.XVName}
                      </Typography>
                    </Box>
                  }
                  subheader={
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {new Date(birthday.birthday).toLocaleDateString()}
                    </Typography>
                  }
                  sx={{ pb: 0, pt: 2, px: 3 }}
                />
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pt: 1 }}>
                  <Typography variant="body2" sx={{ color: '#333', mb: 1 }}>
                    Cliente: {birthday.clientName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333', mb: 1 }}>
                    Evento: {birthday.eventTitle || 'No registrado'}
                  </Typography>
                </CardContent>
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: '#f759ab',
                  }}
                  onClick={() => navigate(`/events/birthday/${birthday.XVID}`)}
                  title={`Visualizar detalles del cumpleaños de ${birthday.XVName}`}
                >
                  <VisibilityIcon />
                </IconButton>
              </Card>
            </Grid>
          </Grid>
          )
        )) : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" sx={{ color: '#4b0082', mb: 2 }}>
              {birthdayList.length === 0 
                ? 'No hay cumpleaños próximos registrados.'
                : 'No se encontraron cumpleaños que coincidan con los filtros aplicados.'
              }
            </Typography>
            {filteredBirthdayList.length === 0 && birthdayList.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                sx={{
                  color: '#f759ab',
                  borderColor: '#f759ab',
                  '&:hover': {
                    backgroundColor: 'rgba(247, 89, 171, 0.1)',
                    borderColor: '#f759ab'
                  }
                }}
              >
                Limpiar Filtros
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Container>
    </div>
  )
}

export default ViewBirthdayList